import { LicenseCost, OutputDatabase } from '@amzn/awscat-portfolio-assessment-types';
import { TabsProps } from '@amzn/awsui-components-react';

import { defaultSortBy } from '../../../common/defaultTableSortState';
import { constants } from '../../../common/Localization';
import { formatCurrencyNoDecimals } from '../../../common/LocalizationHelper';
import AWSCATTable from '../../../common/table/Table-index';
import { ColumnDefinition, ColumnTypes } from '../../../common/table/types';
import { compare, formatRecommendationPaymentOption, roundToInteger } from '../../../common/util';
import { getCell } from '../../common/awsui-table-helper';

export const buildRDSCostDetailsTabs = (
    cashFlowTabContent: JSX.Element,
    pricingTabContent: JSX.Element,
    licenseTabContent: JSX.Element,
): TabsProps.Tab[] => {
    const tabs = [
        {
            id: 'cashFlow',
            label: constants.DATABASE_CASHFLOW,
            content: cashFlowTabContent,
        },
        {
            id: 'pricingDetails',
            label: constants.DATABASE_PRICING_DETAIL,
            content: pricingTabContent,
        },
        {
            id: 'license',
            label: constants.DATABASE_LICENSING,
            content: licenseTabContent,
        }
    ];
    return tabs;
};
    
export const getCashFlowTable = (currency: string, databases: OutputDatabase[], numYears: number): JSX.Element => {
    return (
        <AWSCATTable
            resourceName={constants.INSTANCES}
            items={databases}
            columnDefinitions={getCashFlowColumnDefinitions(currency, numYears)}
            defaultSortBy={defaultSortBy('databaseId', false)}
            autoGenerateVisibleContent
            enableAutoSorting
            useDefaultDownload
        />
    );
};

const baseColumnDefinitions: ColumnDefinition<OutputDatabase>[] = [
    {
        id: 'databaseId',
        header: constants.DATABASE_ID,
        type: ColumnTypes.TEXT,
        cell: item => item.databaseId,
    },
    {
        id: 'instanceType',
        header: constants.INSTANCE_TYPE,
        type: ColumnTypes.TEXT,
        cell: item => item.instanceType,
    },
    {
        id: 'databaseEngine',
        header: constants.DATABASE_ENGINE,
        type: ColumnTypes.TEXT,
        data: item => item.instanceAttributes.databaseEngine,
        cell: item => item.instanceAttributes.databaseEngine,
        sortingComparator: (itemA: OutputDatabase, itemB: OutputDatabase) => {
            const engineA = itemA.instanceAttributes.databaseEngine;
            const engineB = itemB.instanceAttributes.databaseEngine;
            return compare(engineA, engineB);
        }
    },
    {
        id: 'databaseEdition',
        header: constants.EDITION,
        type: ColumnTypes.TEXT,
        data: item => item.instanceAttributes.databaseEdition ?? '-',
        cell: item => item.instanceAttributes.databaseEdition ?? '-',
        sortingComparator: (itemA: OutputDatabase, itemB: OutputDatabase) => {
            const editionA = itemA.instanceAttributes.databaseEdition ?? '';
            const editionB = itemB.instanceAttributes.databaseEdition ?? '';
            return compare(editionA, editionB);
        }
    },
    {
        id: 'recommendedPaymentOption',
        header: constants.RECOMMENDED_PAYMENT,
        type: ColumnTypes.TEXT,
        data: item => formatRecommendationPaymentOption(item.recommendedPaymentOption),
        cell: item => formatRecommendationPaymentOption(item.recommendedPaymentOption),
    },
];

const getCashFlowColumnDefinitions = (currency: string, numYears: number): ColumnDefinition<OutputDatabase>[] => {
    const columns: ColumnDefinition<OutputDatabase>[] = [...baseColumnDefinitions];

    for (let yearIndex = 0; yearIndex < numYears; yearIndex++) {
        columns.push({
            id: `year${yearIndex}`,
            header: `${constants.YEAR} ${yearIndex + 1}`,
            type: ColumnTypes.NUMBER,
            data: item => roundToInteger(item.cashFlow.yearly[yearIndex]),
            cell: item => getCell(formatCurrencyNoDecimals(item.cashFlow.yearly[yearIndex], currency)),
            text: `${constants.YEAR} ${yearIndex + 1}`,
            sortingComparator: (itemA: OutputDatabase, itemB: OutputDatabase) => {
                const costA = itemA.cashFlow.yearly[yearIndex] ?? 0;
                const costB = itemB.cashFlow.yearly[yearIndex] ?? 0;
                return compare(costA, costB);
            }
        });
    }

    columns.push({
        id: 'residual',
        header: constants.RESIDUAL,
        type: ColumnTypes.NUMBER,
        data: item => roundToInteger(item.cashFlow.residual),
        cell: item => getCell(formatCurrencyNoDecimals(item.cashFlow.residual ?? 0, currency)),
        text: constants.RESIDUAL,
        sortingComparator: (itemA: OutputDatabase, itemB: OutputDatabase) => {
            const residualA = itemA.cashFlow.residual ?? 0;
            const residualB = itemB.cashFlow.residual ?? 0;
            return compare(residualA, residualB);
        }
    });
    
    return columns;
};

export const getPricingDetailsTable = (currency: string, databases: OutputDatabase[]): JSX.Element => {
    return (
        <AWSCATTable
            resourceName={constants.PRICING_DETAILS}
            items={databases}
            columnDefinitions={getPricingColumnDefinitions(currency)}
            defaultSortBy={defaultSortBy('databaseId', false)}
            autoGenerateVisibleContent
            enableAutoSorting
            useDefaultDownload
        />
    );
};

const getPricingColumnDefinitions = (currency: string): ColumnDefinition<OutputDatabase>[] => {
    const columns: ColumnDefinition<OutputDatabase>[] = [...baseColumnDefinitions];

    const pricingColumns: ColumnDefinition<OutputDatabase>[] = [
        {
            id: '3yrAu',
            header: `${constants.STANDARD} 3${constants.YR} - ${constants.ALL} ${constants.UPFRONT}`,
            type: ColumnTypes.NUMBER,
            data: item => roundToInteger(item.paymentOptions['3yrAu']?.total),
            cell: item => getCell(item.paymentOptions['3yrAu'] ? formatCurrencyNoDecimals(item.paymentOptions['3yrAu'].total, currency) : '-'),
            text: `${constants.STANDARD} 3${constants.YR} - ${constants.ALL} ${constants.UPFRONT}`,
            sortingComparator: (itemA: OutputDatabase, itemB: OutputDatabase) => {
                const costA = itemA.paymentOptions['3yrAu']?.total ?? 0;
                const costB = itemB.paymentOptions['3yrAu']?.total ?? 0;
                return compare(costA, costB);
            }
        },
        {
            id: '3yrPu',
            header: `${constants.STANDARD} 3${constants.YR} - ${constants.PARTIAL} ${constants.UPFRONT}`,
            type: ColumnTypes.NUMBER,
            data: item => roundToInteger(item.paymentOptions['3yrPu']?.total),
            cell: item => getCell(item.paymentOptions['3yrPu'] ? formatCurrencyNoDecimals(item.paymentOptions['3yrPu'].total, currency) : '-'),
            text: `${constants.STANDARD} 3${constants.YR} - ${constants.PARTIAL} ${constants.UPFRONT}`,
            sortingComparator: (itemA: OutputDatabase, itemB: OutputDatabase) => {
                const costA = itemA.paymentOptions['3yrPu']?.total ?? 0;
                const costB = itemB.paymentOptions['3yrPu']?.total ?? 0;
                return compare(costA, costB);
            }
        },
        {
            id: '1yrAu',
            header: `${constants.STANDARD} 1${constants.YR} - ${constants.ALL} ${constants.UPFRONT}`,
            type: ColumnTypes.NUMBER,
            data: item => roundToInteger(item.paymentOptions['1yrAu']?.total),
            cell: item => getCell(item.paymentOptions['1yrAu'] ? formatCurrencyNoDecimals(item.paymentOptions['1yrAu'].total, currency) : '-'),
            text: `${constants.STANDARD} 1${constants.YR} - ${constants.ALL} ${constants.UPFRONT}`,
            sortingComparator: (itemA: OutputDatabase, itemB: OutputDatabase) => {
                const costA = itemA.paymentOptions['1yrAu']?.total ?? 0;
                const costB = itemB.paymentOptions['1yrAu']?.total ?? 0;
                return compare(costA, costB);
            }
        },
        {
            id: '1yrPu',
            header: `${constants.STANDARD} 1${constants.YR} - ${constants.PARTIAL} ${constants.UPFRONT}`,
            type: ColumnTypes.NUMBER,
            data: item => roundToInteger(item.paymentOptions['1yrPu']?.total),
            cell: item => getCell(item.paymentOptions['1yrPu'] ? formatCurrencyNoDecimals(item.paymentOptions['1yrPu'].total, currency) : '-'),
            text: `${constants.STANDARD} 1${constants.YR} - ${constants.PARTIAL} ${constants.UPFRONT}`,
            sortingComparator: (itemA: OutputDatabase, itemB: OutputDatabase) => {
                const costA = itemA.paymentOptions['1yrPu']?.total ?? 0;
                const costB = itemB.paymentOptions['1yrPu']?.total ?? 0;
                return compare(costA, costB);
            }
        },
        {
            id: '1yrNu',
            header: `${constants.STANDARD} 1${constants.YR} - ${constants.NO} ${constants.UPFRONT}`,
            type: ColumnTypes.NUMBER,
            data: item => roundToInteger(item.paymentOptions['1yrNu']?.total),
            cell: item => getCell(item.paymentOptions['1yrNu'] ? formatCurrencyNoDecimals(item.paymentOptions['1yrNu'].total, currency) : '-'),
            text: `${constants.STANDARD} 1${constants.YR} - ${constants.NO} ${constants.UPFRONT}`,
            sortingComparator: (itemA: OutputDatabase, itemB: OutputDatabase) => {
                const costA = itemA.paymentOptions['1yrNu']?.total ?? 0;
                const costB = itemB.paymentOptions['1yrNu']?.total ?? 0;
                return compare(costA, costB);
            }
        },
        {
            id: 'onDemand',
            header: constants.ON_DEMAND,
            type: ColumnTypes.NUMBER,
            data: item => roundToInteger(item.paymentOptions['onDemand']?.total),
            cell: item => getCell(item.paymentOptions['onDemand'] ? formatCurrencyNoDecimals(item.paymentOptions['onDemand'].total, currency) : '-'),
            text: constants.ON_DEMAND,
            sortingComparator: (itemA: OutputDatabase, itemB: OutputDatabase) => {
                const costA = itemA.paymentOptions['onDemand']?.total ?? 0;
                const costB = itemB.paymentOptions['onDemand']?.total ?? 0;
                return compare(costA, costB);
            }
        }
    ];
    columns.push(...pricingColumns);
    return columns;
};

export const getDatabaseLicensingTable = (currency: string, licenseCosts: LicenseCost[]): JSX.Element => {
    return (
        <AWSCATTable
            resourceName={constants.LICENSES}
            items={licenseCosts}
            columnDefinitions={getDatabaseLicensingColumnDefinitions(currency)}
            defaultSortBy={defaultSortBy('databaseId', false)}
            autoGenerateVisibleContent
            enableAutoSorting
            useDefaultDownload
        />
    );
};

const getDatabaseLicensingColumnDefinitions = (currency: string): ColumnDefinition<LicenseCost>[] => {
    const columns: ColumnDefinition<LicenseCost>[] = [
        {
            id: 'databaseId',
            header: constants.DATABASE_ID,
            type: ColumnTypes.TEXT,
            cell: item => item.databaseId,
            text: constants.DATABASE_ID,
        },
        {
            id: 'databaseEngine',
            header: constants.DATABASE_ENGINE,
            type: ColumnTypes.TEXT,
            cell: item => item.databaseEngine,
            text: constants.DATABASE_ENGINE,
        },
        {
            id: 'databaseEdition',
            header: constants.EDITION,
            type: ColumnTypes.TEXT,
            data: item => item.databaseEdition ?? '-',
            cell: item => item.databaseEdition ?? '-',
            text: constants.EDITION,
        },
        {
            id: 'count',
            header: constants.COUNT,
            type: ColumnTypes.NUMBER,
            cell: item => getCell(item.count),
            text: constants.COUNT,
        },
        {
            id: 'cost',
            header: constants.COST,
            type: ColumnTypes.NUMBER,
            data: item => roundToInteger(item.cost),
            cell: item => getCell(formatCurrencyNoDecimals(item.cost, currency)),
            text: constants.COST,
        },
        {
            id: 'supportCost',
            header: constants.SUPPORT_COST,
            type: ColumnTypes.NUMBER,
            data: item => roundToInteger(item.supportCost),
            cell: item => getCell(formatCurrencyNoDecimals(item.supportCost, currency)),
            text: constants.SUPPORT_COST,
        },
        {
            id: 'totalCost',
            header: constants.TCO_TOTAL_COST,
            type: ColumnTypes.NUMBER,
            data: item => roundToInteger(item.totalCost),
            cell: item => getCell(formatCurrencyNoDecimals(item.totalCost, currency)),
            text: constants.TCO_TOTAL_COST,
        }
    ];
    return columns;
};
